import { Box, Pagination, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { usePractitionerRoleContext } from "../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useDict } from "../../../utils/useDict";
import { useFhirData } from "../../../utils/useFhirData";
import { useFormatter } from "../../../utils/useFormatter";
import { InfoMessage } from "./BackupNotice";

// Monta "estado-cidade, endereço" só com os campos que existirem.
// Se nenhum campo vier preenchido, mostra apenas "-" em vez de um texto quebrado
// (ex.: "undefined-undefined,") ou de quebrar em `.join` de um `line` inexistente.
function formatOrganizationAddress(address: any): string {
  const state = address?.state;
  const city = address?.city;
  const line = Array.isArray(address?.line) ? address.line.filter(Boolean) : [];

  const stateCity = [state, city].filter(Boolean).join("-");
  const lineText = line.join(", ");

  const parts = [stateCity, lineText].filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : "-";
}

// Esse identifier às vezes vem com CPF (11 dígitos) em vez de CNPJ (14 dígitos)
// — ex.: organização cadastrada no nome do próprio responsável. Formata de
// acordo com a quantidade de dígitos em vez de forçar a máscara de CNPJ.
function formatDocument(value: string): string {
  const digits = (value || "").replace(/\D/g, "");
  if (digits.length === 11) return useFormatter.formatCPF(digits);
  return useFormatter.formatCNPJ(digits);
}

export function OwnerCancel() {
  const { user } = useSessionContext();
  const { rolesOrganization, practitionerRole } = usePractitionerRoleContext();

  const responsavelLegal = rolesOrganization
    ?.map((e: any) => e?.resource)
    ?.filter((el: any) => {
      const auxExt =
        el?.extension
          ?.find((ext: any) =>
            ext?.url.includes("StructureDefinition/OrganizationManager")
          )
          ?.extension?.find((i: any) => i?.url === "id")?.valueIdentifier
          ?.value === user?.username;

      return auxExt;
    });

  // function getTrueRoles(obj: Record<string, unknown>): string[] {
  //   return Object.entries(obj)
  //     .filter(([_, value]) => typeof value === "boolean" && value === true)
  //     .map(([key]) => key);
  // }

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 1;

  const handleChangePage = (event: any, newPage: any) => {
    setCurrentPage(newPage);
  };

  return (
    <Stack width="100%" spacing={2}>
      <Stack>
        <Typography variant="subtitle2" textAlign={"center"}>
          Identificamos que você é responsável financeiro pelas seguintes
          organizações:
        </Typography>
      </Stack>

      <Stack spacing={1} width={"100%"}>
        {responsavelLegal
          ?.slice((currentPage - 1) * itemsPerPage, currentPage)
          ?.map((el: any) => {
            const brandLogoOrganization =
              useFhirData?.brandLogoOrganization(el?.extension) ?? "";

            // Mesmo nome usado no Typography abaixo, para a letra inicial bater
            // com o que é exibido quando não há logo.
            const displayName =
              el.alias?.[0] !== "null"
                ? el.alias?.[0]
                : el.name || el?.display || "";

            const auxActivity = practitionerRole
              ?.find((e: any) => {
                e?.resource?.organization?.reference.includes(el?.id)
              }
              )
              ?.resource?.code?.map(
                (role: any) => useDict.roles[role?.coding?.[0]?.system]
              );

            return (
              <Stack
                direction={"row"}
                key={`view-role-${el?.reference?.split("-")?.[1]}`}
                sx={{
                  border: "1px solid",
                  borderColor: "neutral400.main",
                  borderRadius: "8px",
                  padding: "16px",
                }}
                spacing={4}
                width={"100%"}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "68px",
                    height: "68px",
                    borderRadius: "8px",
                    bgcolor: brandLogoOrganization ? "transparent" : "primary700.main",
                  }}
                >
                  {brandLogoOrganization ? (
                    <img
                      alt={el.alias?.[0]}
                      src={brandLogoOrganization}
                      style={{ objectFit: "contain" }}
                      width={"100%"}
                    />
                  ) : (
                    <Typography variant="h5" fontWeight={700} color="neutral100.main">
                      {useFormatter.formatNameFirstInitials(displayName, 1) || "-"}
                    </Typography>
                  )}
                </Box>
                <Stack flex={1} spacing={1}>
                  <Stack direction="row" spacing={4} alignItems="center">
                    <Typography variant="subtitle2">
                      {displayName || "-"}
                    </Typography>
                    <Typography variant="body2" fontStyle={"italic"}>
                      ({formatDocument(el?.identifier?.[0]?.value || "")})
                    </Typography>
                  </Stack>

                  <Typography variant="body1" fontWeight={400}>
                    {formatOrganizationAddress(el.contact?.[0]?.address)}
                  </Typography>
                  <Stack direction="row" alignItems={"center"} spacing={1}>
                    <Typography variant="body2" fontWeight={500}>
                      Atividades: {auxActivity?.join(", ")}
                    </Typography>
                    {/* {trueRoles.map((role) => (
                  <Chip
                    key={role}
                    variant="outlined"
                    size="small"
                    label={role}
                  />
                ))} */}
                  </Stack>
                </Stack>
                {/* <Stack direction="column" justifyContent={"center"}>
              <Tooltip title={"Cancelar assinatura"}>
                <IconButton
                  onClick={async () => {
                    await handleCancelPlan();
                  }}
                >
                  <Close sx={{ color: "red" }} />
                </IconButton>
              </Tooltip>
            </Stack> */}
              </Stack>
            );
          })}
        <Pagination
          count={Math.ceil(
            (responsavelLegal?.length || itemsPerPage) / itemsPerPage
          )}
          page={currentPage}
          onChange={handleChangePage}
          sx={{ mt: 2, display: "flex", justifyContent: "center" }}
          color="primary"
        />
      </Stack>

      <Stack width="100%" spacing={2}>
        <InfoMessage message={"Seus planos de assinatura serão cancelados."} />
        <InfoMessage
          message={"Não será possível marcar novas consultas ou criar agendas."}
        />
        <InfoMessage
          message={
            "Você e os profissionais vinculados as suas organizações terão acesso a plataforma durante 30 dias."
          }
        />
      </Stack>
    </Stack>
  );
}
